/**
 * PDF Export component (legacy name "DocxExport" — actually generates PDFs
 * via server-side PDFKit at /api/generate-pdf-accurate).
 */
import React, { useState, useEffect } from 'react';
import { getQuoteFileStem } from '@shared/quote-naming';
import { buildQuoteJsonExport } from '@shared/quote-json-export';
import { useOfferStore } from '@/lib/store';
import { validateQuoteData } from '@/lib/quote-data-service';
import { getLineNetAfterDiscount, normalizeVatRate, isExcludedFromMainSum } from '@/lib/quote-calculations';
import {
  capturePdfExportSnapshot,
  type PdfExportOptions,
  type PdfExportSnapshot,
} from '@/lib/pdf-export-snapshot';
import { Button } from '@/components/ui/button';
import { Check, FileDown, Loader2, AlertTriangle, FileJson, Sparkles, Eye } from 'lucide-react';
import { PDFViewerModal } from './pdf-viewer-modal';
import { AIProgressModal } from './ai-progress-modal';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PdfSettingsMeta {
  logoFilename?: string;
  agbText?: string;
}

export default function DocxExport() {
  const { toast } = useToast();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfSettingsMeta, setPdfSettingsMeta] = useState<PdfSettingsMeta | null>(null);
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [jsonSuccess, setJsonSuccess] = useState(false);
  const [aiOptimization, setAiOptimization] = useState<'off' | 'light' | 'max'>('off');

  // PDF Viewer Modal state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfMetadata, setPdfMetadata] = useState<{
    customerName?: string;
    offerNumber?: string;
    totalGross?: number;
    itemCount?: number;
    createdAt?: string;
  } | null>(null);

  // AI Progress Modal state
  const [showAiProgress, setShowAiProgress] = useState(false);
  const [pdfExportSnapshotForAi, setPdfExportSnapshotForAi] =
    useState<Readonly<PdfExportSnapshot> | null>(null);
  
  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);
  const discountAmount = useOfferStore((state) => state.discountAmount);
  const discountType = useOfferStore((state) => state.discountType);
  const discountApplicationMode = useOfferStore((state) => state.discountApplicationMode);
  const isDiscountAppliedToPrices = useOfferStore((state) => state.isDiscountAppliedToPrices);
  
  const hasLogoConfigured = Boolean(pdfSettingsMeta?.logoFilename);
  const hasAgbConfigured = Boolean(pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0);
  
  // Load PDF settings on mount
  useEffect(() => {
    const loadPdfSettings = async () => {
      try {
        const response = await fetch('/api/pdf-settings');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setPdfSettingsMeta(data.data || null);
          }
        }
      } catch (error) {
        console.error('Failed to load PDF settings:', error);
      }
    };
    loadPdfSettings();
  }, []);
  
  const captureCurrentPdfExportSnapshot = (
    optionOverrides: Partial<PdfExportOptions> = {},
  ): Readonly<PdfExportSnapshot> => capturePdfExportSnapshot({
    header: quoteHeader,
    items: quoteItems,
    gewerkOrder,
    options: {
      agbText: pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0
        ? pdfSettingsMeta.agbText
        : undefined,
      discountAmount,
      discountType,
      discountApplicationMode,
      isDiscountAppliedToPrices,
      vatRate: normalizeVatRate(quoteHeader.mwstSatz),
      ...optionOverrides,
    },
  });

  // Main PDF Export function (consolidated)
  const handlePDFExport = async () => {
    try {
      setIsGeneratingPDF(true);
      setPdfSuccess(false);
      setError(null);

      console.log("Starting PDF export with subtotals...");

      const snapshot = captureCurrentPdfExportSnapshot(
        aiOptimization === 'off' ? {} : { aiOptimization },
      );

      // Validate the exact data that this export will keep using.
      if (!validateQuoteData(snapshot.header, snapshot.items)) {
        setError("Ungültige Angebotsdaten. Bitte prüfen Sie Ihre Eingaben.");
        setIsGeneratingPDF(false);
        return;
      }

      // If AI optimization is enabled, use the progress modal
      if (aiOptimization !== 'off') {
        setPdfExportSnapshotForAi(snapshot);
        setShowAiProgress(true);
        // The rest will be handled by handleAiComplete
        return;
      }

      // No AI - generate PDF directly
      await generatePdfWithData(snapshot);

    } catch (error) {
      console.error('Error exporting PDF:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`PDF-Fehler: ${errorMessage}`);
      toast({
        title: "PDF-Fehler",
        description: errorMessage,
        variant: "destructive",
      });
      setIsGeneratingPDF(false);
    }
  };

  // Generate PDF from the captured editor snapshot.
  const generatePdfWithData = async (snapshot: Readonly<PdfExportSnapshot>) => {
    try {
      // Generate PDF with accurate subtotals via server
      const response = await fetch('/api/generate-pdf-accurate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: snapshot.header,
          items: snapshot.items,
          gewerkOrder: snapshot.gewerkOrder,
          options: snapshot.options,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server returned ${response.status}`);
      }

      // Get PDF blob
      const blob = await response.blob();
      showPdfPreview(blob, snapshot);

    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Show PDF preview modal
  const showPdfPreview = (blob: Blob, snapshot: Readonly<PdfExportSnapshot>) => {
    const customerName = snapshot.header.kunde?.trim() || 'Kunde';
    const offerNumber = snapshot.header.angebot_nr?.trim() || `A-${new Date().getFullYear()}-XXX`;
    const fileName = `${getQuoteFileStem({ header: snapshot.header }, 'Angebot')}.pdf`;

    const vatRate = normalizeVatRate(snapshot.header.mwstSatz);
    // Hauptsumme wie im PDF: Bedarfs-/Alternativpositionen NICHT einrechnen, sonst
    // zeigt die Vorschau-Metadatenzeile eine andere Endsumme als das PDF selbst.
    const netTotalBeforeGlobalDiscount = snapshot.items
      .filter((item) => !isExcludedFromMainSum(item))
      .reduce((sum, item) => sum + getLineNetAfterDiscount(item), 0);
    const snapshotDiscountAmount = snapshot.options.discountAmount ?? 0;
    const hasGlobalDiscount = snapshot.options.discountApplicationMode === 'total'
      && !snapshot.options.isDiscountAppliedToPrices
      && snapshotDiscountAmount > 0;
    const globalDiscountValueRaw = hasGlobalDiscount
      ? (snapshot.options.discountType === 'percent'
        ? (netTotalBeforeGlobalDiscount * snapshotDiscountAmount) / 100
        : snapshotDiscountAmount)
      : 0;
    const globalDiscountValue = Math.max(0, Math.min(netTotalBeforeGlobalDiscount, Math.round(globalDiscountValueRaw * 100) / 100));
    const netTotal = Math.max(0, Math.round((netTotalBeforeGlobalDiscount - globalDiscountValue) * 100) / 100);
    const totalTax = Math.round(netTotal * (vatRate / 100) * 100) / 100;
    const grossTotal = Math.round((netTotal + totalTax) * 100) / 100;

    // Set state for PDF Viewer Modal
    setPdfBlob(blob);
    setPdfFileName(fileName);
    setPdfMetadata({
      customerName,
      offerNumber,
      totalGross: grossTotal,
      itemCount: snapshot.items.length,
      createdAt: new Date().toISOString(),
    });
    setShowPdfViewer(true);

    setPdfSuccess(true);
    toast({
      title: "PDF erstellt",
      description: "Vorschau wird angezeigt - von dort herunterladen oder drucken.",
      variant: "success",
    });

    setTimeout(() => setPdfSuccess(false), 3000);
  };

  // Handle AI optimization completion
  const handleAiComplete = async (result: { success: boolean; pdfBase64?: string; error?: string }) => {
    const snapshot = pdfExportSnapshotForAi;
    setShowAiProgress(false);
    setIsGeneratingPDF(false);

    if (result.success && result.pdfBase64 && snapshot) {
      // Convert base64 to blob
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      showPdfPreview(blob, snapshot);
    } else {
      setError(result.error || 'KI-Optimierung fehlgeschlagen');
      toast({
        title: "KI-Optimierung fehlgeschlagen",
        description: result.error || 'Unbekannter Fehler',
        variant: "destructive",
      });
    }

    setPdfExportSnapshotForAi(null);
  };

  // JSON Export function
  const handleJSONExport = async () => {
    try {
      setIsExportingJson(true);
      setJsonSuccess(false);
      setError(null);
      
      const snapshot = captureCurrentPdfExportSnapshot();

      // Validate quote data
      if (!validateQuoteData(snapshot.header, snapshot.items)) {
        setError("Ungültige Angebotsdaten. Bitte prüfen Sie Ihre Eingaben.");
        return;
      }
      
      const exportData = buildQuoteJsonExport({
        header: snapshot.header,
        items: snapshot.items,
        gewerkOrder: snapshot.gewerkOrder,
        exportDate: new Date().toISOString(),
      });
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const offerNumber = snapshot.header.angebot_nr?.trim() || `Angebot-${Date.now()}`;
      link.download = `${offerNumber}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      setJsonSuccess(true);
      toast({
        title: "JSON exportiert",
        description: `"${offerNumber}.json" wurde heruntergeladen.`,
        variant: "success",
      });
      
      setTimeout(() => setJsonSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`JSON-Fehler: ${errorMessage}`);
      toast({
        title: "JSON-Fehler",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsExportingJson(false);
    }
  };

  return (
    <div className="space-y-2">
      {/* PDF Vorschau — haeufigster Export, bleibt sichtbar, aber sekundaer
          (Outline): "Speichern" ist der einzige primaere (solid-blaue) Button
          im Editor (Welle 1.1 UX-Entlastung, siehe quote-sidebar.tsx). */}
      <Button
        variant="outline"
        onClick={handlePDFExport}
        disabled={isGeneratingPDF || quoteItems.length === 0}
        className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
        data-testid="button-export-pdf"
      >
        {isGeneratingPDF ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {aiOptimization !== 'off' ? 'KI optimiert...' : 'PDF wird erstellt...'}
          </>
        ) : pdfSuccess ? (
          <>
            <Check className="mr-2 h-4 w-4" />
            PDF bereit
          </>
        ) : (
          <>
            <Eye className="mr-2 h-4 w-4" />
            PDF Vorschau
          </>
        )}
      </Button>

      {/* AI Optimization Option */}
      <div className="flex items-center gap-2">
        <Sparkles className="h-3 w-3 text-amber-500" />
        <Select
          value={aiOptimization}
          onValueChange={(v) => setAiOptimization(v as 'off' | 'light' | 'max')}
          disabled={isGeneratingPDF}
        >
          <SelectTrigger className="h-7 text-xs flex-1" data-testid="select-pdf-ai-optimization">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Keine KI-Optimierung</SelectItem>
            <SelectItem value="light">KI: Leichte Optimierung</SelectItem>
            <SelectItem value="max">KI: MAX (Restrukturierung)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {aiOptimization === 'max' && (
        <p className="text-xs text-orange-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          MAX strukturiert Texte um und kürzt lange Beschreibungen
        </p>
      )}

      {/* Blanko-LV und Großhandel-Anfrage (X83) sind seit Welle 1.3 der
          UX-Entlastung eigenständige Komponenten (blanko-lv-export.tsx,
          grosshandel-anfrage-export.tsx), gebündelt im "Export ▾"-Menü der
          Angebots-Sidebar (quote-sidebar.tsx) — nicht mehr hier gerendert. */}

      {/* Error display */}
      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}
      
      {/* AI Progress Modal */}
      {showAiProgress && pdfExportSnapshotForAi && (
        <AIProgressModal
          isOpen={showAiProgress}
          items={pdfExportSnapshotForAi.items}
          header={pdfExportSnapshotForAi.header}
          gewerkOrder={pdfExportSnapshotForAi.gewerkOrder}
          aiMode={pdfExportSnapshotForAi.options.aiOptimization || 'light'}
          options={pdfExportSnapshotForAi.options}
          onComplete={handleAiComplete}
        />
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        pdfBlob={pdfBlob}
        fileName={pdfFileName}
        metadata={pdfMetadata || undefined}
      />
    </div>
  );
}
